import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail, LOGO_URL, INSTAGRAM_URL } from "../../lib/api";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2, Heart, ExternalLink } from "lucide-react";

function Stars({ value, onChange, size = 34, testidPrefix }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center justify-center gap-1.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          data-testid={testidPrefix ? `${testidPrefix}-${i}` : undefined}
          className="transition-transform active:scale-90"
          aria-label={`${i} sterren`}
        >
          <Star size={size} className={(hover || value) >= i ? "fill-brand-pink text-brand-pink" : "text-brand-border"} />
        </button>
      ))}
    </div>
  );
}

export default function Feedback() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [overall, setOverall] = useState(0);
  const [text, setText] = useState("");
  const [result, setResult] = useState(0);
  const [service, setService] = useState(0);
  const [recommend, setRecommend] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pre = parseInt(params.get("r"));
    if (pre >= 1 && pre <= 5) setOverall(pre);
    api.get(`/feedback/token/${token}`)
      .then(({ data }) => {
        setInfo(data);
        if (data.already_submitted) setDone({ google_review_url: data.google_review_url, overall_rating: 5 });
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!overall) return toast.error("Geef je afspraak een score.");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/feedback/token/${token}`, {
        overall_rating: overall, text, result_rating: result || null,
        service_rating: service || null, recommendation: recommend || null,
      });
      setDone(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-blush flex flex-col items-center px-5 py-14" data-testid="feedback-page">
      <Link to="/" className="mb-8 flex items-center gap-3">
        <img src={LOGO_URL} alt="Nails Renss" className="h-14 w-14 rounded-full" />
        <span className="font-display text-2xl font-semibold text-brand-charcoal">Nails Renss</span>
      </Link>

      <div className="w-full max-w-md bg-white rounded-3xl border border-brand-border shadow-sm p-7">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-pink" size={30} /></div>
        ) : error ? (
          <p className="text-center text-brand-charcoal">{error}</p>
        ) : done ? (
          <div className="text-center" data-testid="feedback-thankyou">
            <div className="h-16 w-16 rounded-full bg-brand-pink/20 flex items-center justify-center mx-auto mb-4">
              <Heart className="text-brand-pink fill-brand-pink" size={30} />
            </div>
            <h1 className="font-display text-3xl font-semibold text-brand-charcoal">Bedankt voor je feedback! 💕</h1>
            <p className="text-brand-muted mt-2 text-sm">Jouw mening betekent veel voor Nails Renss.</p>
            {done.google_review_url && (done.overall_rating >= 4 || info?.already_submitted) && (
              <div className="mt-6">
                {done.overall_rating >= 4 && (
                  <p className="text-sm text-brand-charcoal/80 mb-3">Superfijn om te horen! 💕 Wil je jouw ervaring ook delen op Google? Daarmee help je Nails Renss enorm.</p>
                )}
                <a href={done.google_review_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-brand-charcoal text-white font-semibold px-6 py-3.5 rounded-full hover:bg-brand-pinkhover transition-colors" data-testid="google-review-button">
                  <ExternalLink size={17} /> Laat ook een Google review achter
                </a>
              </div>
            )}
            <Link to="/" className="block mt-6 text-sm text-brand-muted hover:text-brand-charcoal">Terug naar website</Link>
          </div>
        ) : (
          <div>
            <h1 className="font-display text-3xl font-semibold text-brand-charcoal text-center">Hoe was jouw ervaring?</h1>
            <p className="text-center text-brand-muted text-sm mt-1 mb-6">Geef je afspraak een score</p>
            <Stars value={overall} onChange={setOverall} testidPrefix="overall-star" />

            <div className="mt-7">
              <label className="block text-sm font-medium text-brand-charcoal mb-1.5">Wat vond je het leukste aan je ervaring bij Nails Renss?</label>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Schrijf hier kort over je ervaring..." className="w-full rounded-2xl border border-brand-border px-4 py-3 text-brand-charcoal focus:border-brand-pink focus:outline-none resize-none" data-testid="feedback-text" />
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4">
              <div>
                <p className="text-sm font-medium text-brand-charcoal mb-2 text-center">Hoe tevreden ben je over het resultaat?</p>
                <Stars value={result} onChange={setResult} size={26} testidPrefix="result-star" />
              </div>
              <div>
                <p className="text-sm font-medium text-brand-charcoal mb-2 text-center">Hoe was de service?</p>
                <Stars value={service} onChange={setService} size={26} testidPrefix="service-star" />
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-medium text-brand-charcoal mb-2">Zou je Nails Renss aanraden aan iemand anders?</p>
              <div className="flex gap-2">
                {[["ja", "Ja"], ["misschien", "Misschien"], ["nee", "Nee"]].map(([v, l]) => (
                  <button key={v} onClick={() => setRecommend(v)} data-testid={`recommend-${v}`} className={`flex-1 py-2.5 rounded-full text-sm font-medium border transition-colors ${recommend === v ? "bg-brand-charcoal text-white border-brand-charcoal" : "bg-white border-brand-border text-brand-charcoal"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={submit} disabled={submitting || !overall} className="w-full mt-7 bg-brand-charcoal text-white font-semibold py-4 rounded-full disabled:opacity-50 flex items-center justify-center gap-2" data-testid="feedback-submit">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />} Verstuur mijn feedback
            </button>
          </div>
        )}
      </div>
      <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="mt-6 text-sm text-brand-muted hover:text-brand-pinkhover">@nails.renss</a>
    </div>
  );
}
